import { Formik } from "formik";
import { Box } from "@mui/system";
import { View } from "../../constants";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { useParams } from "react-router";
import MUIDataTable from "mui-datatables";
import { useLocation } from "react-router";
import { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { Button, Grid } from "@mui/material";
import { IconAddressBook } from "@tabler/icons";
import EditIcon from "@mui/icons-material/Edit";
import AddPatientReceipts from "./AddPatientReceipts";
import { configState } from "../../../../recoil/atoms";
import EditPatientReceipts from "./EditPatientReceipts";
import LinearProgress from "@mui/material/LinearProgress";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AppointmentApi } from "../../../../api/medical-operations";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../../utils/FormFields/ConfirmDialog";
import useAuthorization from "../../../Authentication/permissions/Authorization";
import { GetReceiptResponseReceipts, ReceiptApi } from "../../../../api/payment";
import AutocompletePatient from "../../../MedicalOperations/Appointments/FormFields/AutocompletePatient";


export default function PatientReceipts() {
  const { pathname }  = useLocation();
  const config = useRecoilValue(configState);
  let { patientID, appointmentID } = useParams();
  const isGeneralReceipt = pathname.includes("payment");

  const [count, setCount] = useState<number>();
  const [rowID, setRowID] = useState<string>("");
  const [pID, setPID] = useState<string>(patientID!);
  const [view, setView] = useState<View>(View.TABLE);
  const [editInfo, setEditInfo] = useState<any | any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [patientReceipt, setPatientReceipt] = useState<GetReceiptResponseReceipts[]>([]);
  const [selectedPatientID, setSelectedPatientID] = useState<string | undefined>(undefined);

  const { canAddPatientReceipts, canGetPatientReceipts, canEditPatientReceipts, canDeletePatientReceipts } = useAuthorization();

  const getPatientFromAppointment = () => {
    const appointment = new AppointmentApi(config);
    appointment
      .getAppointmentById(appointmentID!)
      .then((res) => {
        setPID(res.data.Patient.PatientID);
        getPatientReceipts(res.data.Patient.PatientID);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  const getPatientReceipts = (pID?: string, offset?: number, limit?: number) => {
    const PatientReceipts = new ReceiptApi(config);
    PatientReceipts
      .getReceipt(undefined, pID, undefined, offset, limit)
      .then((res) => {
        setPatientReceipt(res?.data.receipts!);
        setCount(res?.data?.total);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!patientID && appointmentID) getPatientFromAppointment();
    else getPatientReceipts(selectedPatientID || patientID);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view!, selectedPatientID]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deletePatientReceipt(rowID);
    handleCloseDialog();
  };

  const deletePatientReceipt = (id: string) => {
    const PatientReceipts = new ReceiptApi(config);
    PatientReceipts.deleteReceipt(id)
      .then((res) => {
        getPatientReceipts(pID);
        toast.success(res?.data?.message);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const handlePageChange = (tableState: any) => {
    const { page, rowsPerPage } = tableState;    
    const newOffset = page * rowsPerPage;    

    getPatientReceipts(selectedPatientID, newOffset, rowsPerPage)
  };

  const handleChangeRowsPerPage = (tableState: any) => {    
    const { page, rowsPerPage } = tableState;

    getPatientReceipts(selectedPatientID, page * rowsPerPage, rowsPerPage)
  };

  const columns = [
    {
      name: "receipt_id",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },
    {
      name: "patient_id",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },
    {
      name: "first_name",
      label: "First Name",
      options: {
        filter: true,
      },
    },
    {
      name: "middle_name",
      label: "Middle Name",
      options: {
        filter: true,
      },
    },
    {
      name: "family_name",
      label: "Family Name",
      options: {
        filter: true,
      },
    },
    {
      name: "amount",
      label: "Amount in USD",
    },
    {
      name: "created_at",
      label: "Date Created",
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => <>{value.slice(0, 10)}</>,
      },
    },
    {
      name: "updated_at",
      label: "Date Updated",
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => <>{value.slice(0, 10)}</>,
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
            {canEditPatientReceipts && (
              <Tooltip title="Edit">
                <Button
                  color="secondary"
                  onClick={() => {
                    setView(View.EDIT);
                    setEditInfo(tableMeta.tableData[tableMeta.rowIndex]);
                  }}
                >
                  <EditIcon color="secondary" />
                </Button>
              </Tooltip>
            )}
            {canDeletePatientReceipts && (
              <Tooltip title="Delete">
                <Button
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },
                  }}
                  onClick={() => {
                    handleDelete(tableMeta.rowData[0]);
                  }}
                >
                  <DeleteOutlineIcon color="error" />
                </Button>
              </Tooltip>
            )}
          </>
        ),
      },
    },
  ];

  return (
    <>
    {canGetPatientReceipts && (
      <MainCard>
        {!isLoading ? (
          <>
            <Button
              disabled={view === View.TABLE}
              variant="outlined"
              color="secondary"
              onClick={() => setView(View.TABLE)}
              sx={{ mb: 2 }}
            >
              View Receipts
            </Button>

            {view === View.TABLE ? (
              <Grid>
                <>
                  {isGeneralReceipt && (
                    <Formik
                      initialValues={{}}
                      enableReinitialize={true}                    
                      onSubmit={() => {}}
                    >
                      <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
                        <AutocompletePatient 
                          name="patient_id"
                          setSelectedPatientID={setSelectedPatientID}
                        />
                      </Grid>
                    </Formik>
                  )}   
                  <MUIDataTable
                    data={patientReceipt}
                    columns={columns}
                    options={{
                      serverSide: true,
                      filter: false,
                      viewColumns: false,
                      selectableRows: "none",
                      selectableRowsOnClick: false,
                      elevation: 0,
                      rowsPerPageOptions: [10, 20, 50],
                      count: count,                                            
                      onTableChange: async (action, tableMeta) => {                                                                                                                                           
                        switch (action) {
                          case "changePage":
                            handlePageChange(tableMeta);
                            break;
                          case "changeRowsPerPage":
                            handleChangeRowsPerPage(tableMeta);
                            break;                          
                          default:                          
                            break;
                        }                   
                      },  
                      filterType: "dropdown",
                      search: selectedPatientID !== undefined,
                      enableNestedDataAccess: ".",
                      responsive: "standard",
                      expandableRows: false,
                      customToolbar: () => (
                        canAddPatientReceipts && (
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<IconAddressBook />}
                            onClick={() => {
                              setView(View.ADD);
                            }}
                            sx={{
                              ml: "50px",
                            }}
                          >
                            Add Receipt
                          </Button>
                        )
                      ),
                    }}
                    title=""
                  />
                  <ConfirmDialog
                    name={"Patient Receipt"}
                    handleCloseDialog={handleCloseDialog}
                    handleConfirm={handleConfirm}
                    openDialog={openDialog}
                  />
                </>
              </Grid>
            ) : view === View.ADD ? (
              <Box>
                <AddPatientReceipts patientID={pID} />
              </Box>
            ) : (
              <EditPatientReceipts initialPatientReceiptValues={editInfo} />
            )}
          </>
        ) : (
          <LinearProgress color="secondary" />
        )}
      </MainCard>
    )}
    </>
  );
}